<template>
    <div class="ticket-generator">
        <!-- Aviso de conexión: el kiosco reintenta solo hasta reconectar -->
        <div v-if="!conectado" class="banner-conexion">
            ⚠ {{ motivoDesconexion || 'Sin conexión con el servidor. Reconectando…' }}
        </div>
        <!-- Error al generar el ticket (nunca se imprime un ticket falso) -->
        <div v-if="errorTicket" class="toast-error-ticket">{{ errorTicket }}</div>

        <!-- ===== PANTALLA DE INICIO (bienvenida) ===== -->
        <Transition name="fade-inicio">
            <div v-if="pantallaInicio" class="pantalla-bienvenida" @click="comenzar">
                <template v-if="imagenBienvenidaOk">
                    <img
                        src="/img/bienvenida.png"
                        alt="Bienvenido a la Dirección Departamental de Educación del Beni"
                        class="bienvenida-img"
                        draggable="false"
                        @error="imagenBienvenidaOk = false"
                    >
                    <div class="bienvenida-pulso"></div>
                </template>

                <!-- Respaldo si no se encuentra public/img/bienvenida.png -->
                <div v-else class="bienvenida-respaldo">
                    <img src="/img/logo_minedu.png" alt="Logo DDE Beni" class="respaldo-logo">
                    <h1 class="respaldo-titulo">BIENVENIDO</h1>
                    <p class="respaldo-sub">
                        A LA DIRECCIÓN DEPARTAMENTAL<br>
                        <span>DE EDUCACIÓN DEL BENI</span>
                    </p>
                    <div class="respaldo-boton">
                        TOCA LA PANTALLA<br>
                        <span>PARA INICIAR</span>
                    </div>
                </div>
            </div>
        </Transition>

        <!-- HEADER: Logo y título + Reloj -->
        <div class="header">
            <div class="logo-area">
                <img src="/img/logo_minedu.png" alt="Logo DDE Beni" class="logo">
                <div class="title-area">
                    <h1>SISTEMA GESTION <br> DE COLAS</h1>
                    <p>GENERADOR DE TICKETS</p>
                </div>
            </div>
            <div class="reloj-area">
                <div class="fecha">{{ fechaActual }}</div>
                <div class="hora">{{ horaActual }}</div>
            </div>
        </div>

        <!-- CONTENIDO PRINCIPAL -->
        <div class="contenido-principal">
            <!-- Loading -->
            <div v-if="loading" class="loading">
                <div class="spinner"></div>
                <p>Cargando...</p>
            </div>

            <div v-else>
                <!-- ===== STEP 1: TIPO DE TRÁMITE ===== -->
                <div v-if="currentStep === 1 && !ticketGenerado" class="step-container">
                    <h2 class="step-title">Seleccione el tipo de trámite</h2>
                    <p class="step-subtitle">Por favor, elija la opción para continuar</p>

                    <div class="tramites-grid">
                        <!-- Información de Trámites -->
                        <button
                            @click="selectTipoTramite(1)"
                            class="tramite-btn tramite-info"
                            :class="{ selected: selectedTipoTramite === 1 }"
                        >
                            <div class="btn-icon-large">
                                <svg fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                                </svg>
                            </div>
                            <div class="btn-content">
                                <span class="btn-title">Información de Trámites</span>
                                <span class="btn-desc">Consultas y orientación sobre trámites</span>
                            </div>
                            <div v-if="selectedTipoTramite === 1" class="selected-check">✓</div>
                        </button>

                        <!-- Recojo de Trámites -->
                        <button
                            @click="selectTipoTramite(2)"
                            class="tramite-btn tramite-recojo"
                            :class="{ selected: selectedTipoTramite === 2 }"
                        >
                            <div class="btn-icon-large">
                                <svg fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                                </svg>
                            </div>
                            <div class="btn-content">
                                <span class="btn-title">Recojo de Trámites</span>
                                <span class="btn-desc">Retiro de documentos y trámites finalizados</span>
                            </div>
                            <div v-if="selectedTipoTramite === 2" class="selected-check">✓</div>
                        </button>

                        <!-- Recepción de Trámites -->
                        <button
                            @click="selectTipoTramite(3)"
                            class="tramite-btn tramite-recepcion"
                            :class="{ selected: selectedTipoTramite === 3 }"
                        >
                            <div class="btn-icon-large">
                                <svg fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                                </svg>
                            </div>
                            <div class="btn-content">
                                <span class="btn-title">Recepción de Trámites</span>
                                <span class="btn-desc">Presentación de nuevos trámites y solicitudes</span>
                            </div>
                            <div v-if="selectedTipoTramite === 3" class="selected-check">✓</div>
                        </button>

                        <!-- Canje de Valores -->
                        <button
                            @click="selectTipoTramite(4)"
                            class="tramite-btn tramite-canje"
                            :class="{ selected: selectedTipoTramite === 4 }"
                        >
                            <div class="btn-icon-large">
                                <svg fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                                </svg>
                            </div>
                            <div class="btn-content">
                                <span class="btn-title">Canje de Valores</span>
                                <span class="btn-desc">Canje de documentos y valores</span>
                            </div>
                            <div v-if="selectedTipoTramite === 4" class="selected-check">✓</div>
                        </button>
                    </div>
                </div>

                <!-- ===== STEP 2: PREFERENCIA ===== -->
                <div v-if="currentStep === 2 && !ticketGenerado" class="step-container step-preferencia">
                    <button @click="prevStep" class="back-btn">← Volver</button>

                    <h2 class="step-title step-title-preferencia">Seleccione su preferencia</h2>
                    <p class="step-subtitle step-subtitle-preferencia">Esto nos ayuda a priorizar su atención</p>

                    <!-- Botones: Identificarse -->
                    <div class="usuario-actions">
                        <button
                            @click="modoUsuario = 'identificarse'; mostrarModalIdentificacion = true"
                            class="btn-usuario btn-identificarse"
                            :class="{ active: modoUsuario === 'identificarse' }"
                        >
                            🔍 Identificarse
                        </button>
                    </div>

                    <!-- Usuario confirmado -->
                    <div v-if="usuarioConfirmado && datosUsuario" class="usuario-confirmado">
                        <div class="usuario-info">
                            <span class="usuario-icon">✅</span>
                            <div>
                                <div class="usuario-documento">CI: {{ datosUsuario.documento }}</div>
                                <div class="usuario-nombre">{{ datosUsuario.nombre_completo }}</div>
                            </div>
                            <button @click="limpiarUsuario" class="usuario-cambiar">Cambiar</button>
                        </div>
                    </div>

                    <!-- Grid de preferencias -->
                    <div class="preferencias-grid">
                        <button
                            v-for="preferencia in preferencias"
                            :key="preferencia.id"
                            @click="selectAndGenerate(preferencia)"
                            class="preferencia-btn"
                            :class="getPreferenciaColorClass(preferencia.id)"
                        >
                            <div class="pref-icon-small">
                                <svg v-if="preferencia.id === 1" fill="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="7" r="4"/>
                                    <path d="M5.5 21c0-3.59 2.91-6.5 6.5-6.5s6.5 2.91 6.5 6.5H5.5z"/>
                                </svg>
                                <svg v-else-if="preferencia.id === 2" fill="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="5" r="3"/>
                                    <path d="M9.5 10c-.8 0-1.5.7-1.5 1.5V15h2v7h2v-7h1.5v-3.5c0-.8-.7-1.5-1.5-1.5H9.5z"/>
                                    <path d="M14 15h1.5l1.5 6h-2l-1-6z"/>
                                </svg>
                                <svg v-else-if="preferencia.id === 3" fill="currentColor" viewBox="0 0 24 24">
                                    <circle cx="11.5" cy="4.5" r="3"/>
                                    <path d="M8 9.5h5.5c1.1 0 2 .9 2 2v1.5c0 2-1.3 3.7-3 4.3V22h-2v-4.8c-1.6-.7-2.5-2.3-2.5-4V11.5c0-1.1.9-2 2-2z"/>
                                    <ellipse cx="13.5" cy="13" rx="2" ry="2.5"/>
                                </svg>
                                <svg v-else-if="preferencia.id === 4" fill="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="4" r="2.5"/>
                                    <path d="M10 8h2.5c1 0 1.8.7 2 1.6l1 4.4H18v2h-3c-.9 0-1.7-.6-1.9-1.5L12.2 12H10c-1.1 0-2-.9-2-2v-1c0-.6.4-1 1-1z"/>
                                    <path fill-rule="evenodd" d="M9 13l-1 3.5A4.5 4.5 0 1014.8 19l.7 1.7A6.5 6.5 0 117 16l.2-.6"/>
                                </svg>
                            </div>
                            <div class="pref-content">
                                <span class="pref-title">{{ preferencia.nombre }}</span>
                                <span class="pref-desc">Prioridad: {{ getPriorityText(preferencia.nivel_prioridad) }}</span>
                            </div>
                            <div v-if="selectedPreferencia?.id === preferencia.id" class="pref-check">✓</div>
                        </button>
                    </div>

                    <!-- Mensaje de información -->
                    <div class="info-message">
                        <div class="info-icon">ℹ️</div>
                        <div class="info-content">
                            <strong>Seleccione su preferencia</strong>
                            <p>Al seleccionar, se generará su ticket automáticamente.</p>
                        </div>
                    </div>
                </div>

                <!-- ===== TICKET GENERADO ===== -->
                <div v-if="ticketGenerado" class="ticket-result-container">
                    <div class="ticket-result-card">
                        <div class="success-header">
                            <h3>¡Ticket Generado Exitosamente!</h3>
                        </div>
                        <div class="success-body">
                            <div class="ticket-info">
                                <div class="info-row">
                                    <div>
                                        <span class="info-label">Número de Ticket</span>
                                        <span class="info-value ticket-number">{{ ticketGenerado.numero_ticket }}</span>
                                    </div>
                                    <div>
                                        <span class="info-label">Posición en cola</span>
                                        <span class="info-value position-highlight">#{{ posicionEnCola }}</span>
                                    </div>
                                </div>
                                <div class="info-row">
                                    <div>
                                        <span class="info-label">Tipo de Trámite</span>
                                        <span class="info-value">{{ getTipoTramiteNombre }}</span>
                                    </div>
                                    <div>
                                        <span class="info-label">Preferencia</span>
                                        <span class="info-value">{{ selectedPreferencia?.nombre }}</span>
                                    </div>
                                </div>
                                <div class="info-row">
                                    <div>
                                        <span class="info-label">Fecha y Hora</span>
                                        <span class="info-value">{{ formatDateTime(ticketGenerado.fecha_hora_solicitud) }}</span>
                                    </div>
                                </div>
                                <div v-if="datosUsuario" class="info-row">
                                    <div>
                                        <span class="info-value">CI: {{ datosUsuario.documento }}</span>
                                    </div>
                                    <div>
                                        <span class="info-value">Usuario: {{ datosUsuario.nombre_completo }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="instructions-card">
                                <div class="instructions-title">📋 Instrucciones importantes</div>
                                <ul class="instructions-list">
                                    <li>✅ Espere su turno en las pantallas digitales</li>
                                    <li>🔊 Esté atento al llamado por voz</li>
                                    <li>📄 Presente este ticket en la ventanilla indicada</li>
                                    <li>⏰ Su turno será llamado en orden de prioridad</li>
                                </ul>
                            </div>

                            <div class="button-group">
                                <button @click="printTicket(true)" class="print-btn">🖨️ Reimprimir</button>
                                <button @click="resetForm" class="new-ticket-btn">➕ Nuevo Ticket</button>
                            </div>

                            <div class="retorno-aviso">
                                Su ticket se imprimió automáticamente. Retírelo de la impresora.<br>
                                Volviendo al inicio en <strong>{{ segundosRetorno }}</strong> s
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ===== MODAL IDENTIFICACIÓN ===== -->
        <div v-if="mostrarModalIdentificacion" class="modal-overlay" @click.self="cerrarModal">
            <div class="modal-content">
                <div class="modal-header">
                    <h3>Identificarse</h3>
                    <button @click="cerrarModal" class="modal-close">✕</button>
                </div>
                <div class="modal-body">
                    <div class="modal-documento">
                        <label>Número de Documento de Identidad</label>
                        <div class="modal-input-group">
                            <input
                                v-model="modalDocumento"
                                type="text"
                                placeholder="Ej: 123456789"
                                readonly
                                @focus="modalDocumentoFocus = true"
                            >
                            <button
                                v-if="modalDocumento"
                                @click="limpiarUsuarioIdif"
                                class="modal-limpiar"
                            >
                                ✕
                            </button>
                            <button
                                v-if="!modalExito"
                                @click="buscarUsuario"
                                class="modal-btn-buscar"
                            >
                                Buscar
                            </button>
                        </div>
                        <div v-if="modalError" class="modal-error">{{ modalError }}</div>
                        <div v-if="modalExito && modoUsuario === 'identificarse'" class="modal-exito">
                            ✅ Usuario encontrado: <strong>{{ modalUsuarioEncontrado?.nombre_completo }}</strong>
                        </div>
                    </div>

                    <div class="modal-actions">
                        <button
                            v-if="modoUsuario === 'identificarse' && modalExito"
                            @click="confirmarIdentificacion"
                            class="modal-btn-confirmar"
                        >
                            Confirmar identidad
                        </button>
                    </div>
                </div>

                <!-- Teclado Numérico -->
                <div class="modal-teclado">
                    <div class="teclado-grid">
                        <button v-for="n in 9" :key="n" @click="agregarNumeroModal(n)" class="tecla">{{ n }}</button>
                        <button @click="agregarNumeroModal(0)" class="tecla">0</button>
                        <button @click="eliminarNumeroModal" class="tecla tecla-borrar">⌫</button>
                        <button @click="cerrarTecladoModal" class="tecla tecla-aceptar">✓</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import axios from 'axios'

// --- Kiosco: clave secreta (viene en ?k= desde el .bat) -------------------
// Se guarda en sessionStorage y se envía en el header de TODAS las peticiones (ver `api`).
const KIOSCO_STORAGE_KEY = 'kiosco_token'
let kioscoToken = new URLSearchParams(window.location.search).get('k')
try {
    if (kioscoToken) sessionStorage.setItem(KIOSCO_STORAGE_KEY, kioscoToken)
    else kioscoToken = sessionStorage.getItem(KIOSCO_STORAGE_KEY)
} catch (e) { /* sessionStorage no disponible */ }

// Cliente HTTP propio del kiosco: apunta a la API (/api/...) y envía la clave en cada
// petición. No toca el axios global del resto del sistema.
// Si su API no usa el prefijo /api, cambie API_BASE (p. ej. '' para rutas sin prefijo).
const API_BASE = '/api'
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        ...(kioscoToken ? { 'X-Kiosco-Token': kioscoToken } : {})
    }
})

const loading = ref(true)
const currentStep = ref(1)
const selectedTipoTramite = ref(null)
const preferencias = ref([])
const selectedPreferencia = ref(null)
const ticketGenerado = ref(null)
const posicionEnCola = ref(null)

// Reloj
const fechaActual = ref('')
const horaActual = ref('')
let relojInterval = null

// Estado del usuario
const modoUsuario = ref('identificarse')
const usuarioConfirmado = ref(false)
const datosUsuario = ref(null)

// Modal
const mostrarModalIdentificacion = ref(false)
const modalDocumento = ref('')
const modalDocumentoFocus = ref(false)
const modalError = ref('')
const modalExito = ref(false)
const modalUsuarioEncontrado = ref(null)

// ===== PANTALLA DE INICIO E INACTIVIDAD =====
// La pantalla de inicio se muestra al abrir la página, después de generar el
// ticket (tras RETORNO_TICKET_S segundos) y tras INACTIVIDAD_MS sin tocar nada.
const INACTIVIDAD_MS = 15000      // 15 s sin actividad → vuelve a la pantalla de inicio
const RETORNO_TICKET_S = 10       // segundos que se muestra el ticket antes de volver al inicio
const pantallaInicio = ref(true)
const imagenBienvenidaOk = ref(true)
const segundosRetorno = ref(RETORNO_TICKET_S)
let inactividadTimer = null
let retornoTicketTimer = null
let generandoTicket = false       // evita generar (e imprimir) dos tickets con doble toque

// ===== CONEXIÓN CON EL SERVIDOR (reconexión automática) =====
// El kiosco nunca se "desconecta" por inactividad: un latido (heartbeat) consulta al
// servidor cada 30 s. Si el servidor no responde, se
// bloquea el inicio, se muestra un aviso y se reintenta cada 5 s hasta reconectar.
// JAMÁS se genera un ticket falso: si no hay servidor no hay ticket.
const HEARTBEAT_OK_MS = 30000
const HEARTBEAT_FALLO_MS = 5000
const RECARGA_PAGINA_MS = 6 * 60 * 60 * 1000   // recarga preventiva (solo en inicio y con conexión)
const inicioPagina = Date.now()
const conectado = ref(true)
const motivoDesconexion = ref('')
const errorTicket = ref('')
let fallosSeguidos = 0
let heartbeatTimer = null
let errorTicketTimer = null

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const mostrarErrorTicket = (mensaje) => {
    errorTicket.value = mensaje
    clearTimeout(errorTicketTimer)
    errorTicketTimer = setTimeout(() => { errorTicket.value = '' }, 8000)
}

// Consulta ligera al servidor para saber si hay conexión y refrescar las preferencias.
const verificarConexion = async () => {
    try {
        const r = await api.get('/tickets/initial-data', { timeout: 8000, params: { _t: Date.now() } })
        if (r.data?.success) {
            const nuevas = r.data.data.preferencias
            if (JSON.stringify(nuevas) !== JSON.stringify(preferencias.value)) preferencias.value = nuevas
        }
        fallosSeguidos = 0
        conectado.value = true
        motivoDesconexion.value = ''
        return true
    } catch (e) {
        const status = e.response?.status
        if (status === 429) {
            // Servidor alcanzable pero limitando peticiones: no es una desconexión.
            return false
        } else if (status === 401) {
            fallosSeguidos = 2
            conectado.value = false
            motivoDesconexion.value = 'Kiosco no autorizado: revise la clave de acceso.'
        } else if (status === 503) {
            fallosSeguidos = 2
            conectado.value = false
            motivoDesconexion.value = 'Kiosco no configurado en el servidor.'
        } else {
            fallosSeguidos++
            if (fallosSeguidos >= 2) {
                conectado.value = false
                motivoDesconexion.value = 'Sin conexión con el servidor. Reconectando…'
            }
        }
        return false
    }
}

const programarHeartbeat = () => {
    clearTimeout(heartbeatTimer)
    heartbeatTimer = setTimeout(async () => {
        await verificarConexion()
        // Recarga preventiva (p. ej. tras actualizar el sistema); nunca sin conexión,
        // para no caer en la página de error de Chrome.
        if (conectado.value && pantallaInicio.value && !ticketGenerado.value &&
            Date.now() - inicioPagina > RECARGA_PAGINA_MS) {
            window.location.reload()
            return
        }
        programarHeartbeat()
    }, conectado.value ? HEARTBEAT_OK_MS : HEARTBEAT_FALLO_MS)
}

const comprobarYa = async () => {
    await verificarConexion()
    programarHeartbeat()
}

// POST /tickets/generate con reconexión: ante límite de peticiones (429), 419 o corte de red,
// vuelve a conectar y reintenta (hasta 3 veces). Los demás errores no se reintentan.
const postGenerar = async (data) => {
    const MAX_INTENTOS = 3
    for (let intento = 1; intento <= MAX_INTENTOS; intento++) {
        try {
            return await api.post('/tickets/generate', data, { timeout: 10000 })
        } catch (error) {
            const status = error.response?.status
            const sinRespuesta = !error.response && error.code !== 'ECONNABORTED' && error.code !== 'ETIMEDOUT'
            const recuperable = status === 429 || status === 419 || sinRespuesta
            if (!recuperable || intento === MAX_INTENTOS) throw error
            // 429: respetar Retry-After (máx. 5 s); resto: espera creciente
            const retryAfter = Number(error.response?.headers?.['retry-after']) || 0
            await esperar(status === 429 ? Math.min(Math.max(retryAfter, 1), 5) * 1000 : 1500 * intento)
            await verificarConexion()   // confirma que hay servidor antes de reintentar
        }
    }
}

const detenerInactividad = () => {
    clearTimeout(inactividadTimer)
    inactividadTimer = null
}

const reiniciarInactividad = () => {
    detenerInactividad()
    // Sin temporizador en la pantalla de inicio ni en la del ticket (esa tiene su propio retorno)
    if (pantallaInicio.value || ticketGenerado.value) return
    inactividadTimer = setTimeout(() => volverAlInicio(), INACTIVIDAD_MS)
}

const detenerRetornoTicket = () => {
    clearInterval(retornoTicketTimer)
    retornoTicketTimer = null
}

const iniciarRetornoTicket = () => {
    detenerRetornoTicket()
    segundosRetorno.value = RETORNO_TICKET_S
    retornoTicketTimer = setInterval(() => {
        segundosRetorno.value -= 1
        if (segundosRetorno.value <= 0) volverAlInicio()
    }, 1000)
}

const comenzar = () => {
    if (!conectado.value) return   // sin servidor no se puede sacar ticket
    pantallaInicio.value = false
    currentStep.value = 1
    reiniciarInactividad()
}

const volverAlInicio = () => {
    detenerInactividad()
    detenerRetornoTicket()
    pantallaInicio.value = true      // primero se activa la pantalla de inicio...
    mostrarModalIdentificacion.value = false
    resetForm()                      // ...y luego se limpia todo el formulario
}

// Cualquier toque, clic o tecla reinicia el contador de inactividad
const registrarActividad = () => reiniciarInactividad()

// Al mostrarse el ticket generado: impresión automática + cuenta regresiva al inicio
watch(ticketGenerado, (ticket) => {
    if (!ticket) return
    detenerInactividad()
    iniciarRetornoTicket()
    nextTick(() => printTicket(false))
})

// Computed
const getTipoTramiteNombre = computed(() => {
    const tipos = {
        1: 'Información de Trámites',
        2: 'Recojo de Trámites',
        3: 'Recepción de Trámites',
        4: 'Canje de Valores'
    }
    return tipos[selectedTipoTramite.value] || ''
})

// Funciones del reloj
const formatearFecha = () => {
    const now = new Date()
    const opciones = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
    fechaActual.value = now.toLocaleDateString('es-ES', opciones)
        .replace(/^\w/, (c) => c.toUpperCase())
}

const formatearHora = () => {
    const now = new Date()
    horaActual.value = now.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
}

const iniciarReloj = () => {
    formatearFecha()
    formatearHora()
    relojInterval = setInterval(() => {
        formatearHora()
    }, 1000)
}

// ===== FUNCIONES DE NAVEGACIÓN =====
const selectTipoTramite = (id) => {
    selectedTipoTramite.value = id
    // Avanzar automáticamente al paso 2 después de seleccionar
    setTimeout(() => {
        // Si mientras tanto volvió la pantalla de inicio, no avanzar
        if (!pantallaInicio.value && selectedTipoTramite.value === id) {
            currentStep.value = 2
        }
    }, 300) // Pequeño delay para ver la selección
}

const prevStep = () => {
    currentStep.value = 1
}

// ===== FUNCIONES DE PREFERENCIAS =====
const getPreferenciaColorClass = (id) => {
    const colors = {
        1: 'preferencia-normal',
        2: 'preferencia-mayor',
        3: 'preferencia-madre',
        4: 'preferencia-discapacidad'
    }
    return colors[id] || 'preferencia-normal'
}

const getPreferenciaEmoji = (id) => {
    const emojis = {
        1: '👤',
        2: '👴',
        3: '👶',
        4: '♿'
    }
    return emojis[id] || '📌'
}

const selectAndGenerate = async (preferencia) => {
    if (generandoTicket) return
    generandoTicket = true
    selectedPreferencia.value = preferencia
    try {
        await generateTicket()
    } finally {
        generandoTicket = false
    }
}

const generateTicket = async () => {
    if (!selectedPreferencia.value || !selectedTipoTramite.value) {
        alert('Seleccione un tipo de trámite y preferencia')
        return
    }

    const data = {
        id_preferencia: selectedPreferencia.value.id,
        id_tipo_tramite: selectedTipoTramite.value
    }

    if (usuarioConfirmado.value && datosUsuario.value) {
        data.id_usuario = datosUsuario.value.id
    }

    try {
        const response = await postGenerar(data)

        if (response.data.success) {
            ticketGenerado.value = response.data.data.ticket
            posicionEnCola.value = response.data.data.posicion_espera
        } else {
            mostrarErrorTicket('No se pudo generar el ticket. Intente nuevamente.')
        }
    } catch (error) {
        // NO se inventa ningún ticket: si falla, se avisa y se puede volver a intentar.
        const status = error.response?.status
        if (status === 401) {
            mostrarErrorTicket('Kiosco no autorizado. Avise al personal.')
        } else if (status === 422) {
            const errores = error.response?.data?.errors
            const detalle = errores
                ? Object.entries(errores).map(([campo, msgs]) => `${campo}: ${[].concat(msgs)[0]}`).join(' · ')
                : (error.response?.data?.message || '')
            console.error('Validación /tickets/generate:', error.response?.data)
            mostrarErrorTicket('Datos inválidos' + (detalle ? ` (${detalle})` : '') + '. Vuelva al inicio e intente de nuevo.')
        } else {
            mostrarErrorTicket('No se pudo generar el ticket. Intente nuevamente en unos segundos.')
        }
        verificarConexion()   // actualiza el estado de conexión (y muestra el aviso si cayó)
    }
}

// Funciones del Modal
const buscarUsuario = async () => {
    if (!modalDocumento.value || modalDocumento.value.length < 5) {
        modalError.value = 'Ingrese un número de documento válido'
        return
    }

    modalError.value = ''

    try {
        const response = await api.post('/tickets/buscar-usuario', {
            documento: modalDocumento.value
        })

        if (response.data.success) {
            modalExito.value = true
            modalUsuarioEncontrado.value = response.data.data
            modalError.value = ''
        }
    } catch (error) {
        if (error.response?.status === 404) {
            modalError.value = 'Usuario no encontrado. Verifique el número de documento.'
            modalExito.value = false
        } else {
            modalError.value = 'Error al buscar usuario. Intente nuevamente.'
        }
    }
}

const confirmarIdentificacion = () => {
    if (modalUsuarioEncontrado.value) {
        datosUsuario.value = modalUsuarioEncontrado.value
        usuarioConfirmado.value = true
        cerrarModal()
    }
}

const limpiarUsuarioIdif = () => {
    usuarioConfirmado.value = false
    datosUsuario.value = null
    modalExito.value = false
    modalUsuarioEncontrado.value = null
    modalDocumento.value = ''
}

const limpiarUsuario = () => {
    usuarioConfirmado.value = false
    datosUsuario.value = null
}

const cerrarModal = () => {
    mostrarModalIdentificacion.value = false
    modalDocumentoFocus.value = false
}

// Funciones del teclado numérico
const agregarNumeroModal = (num) => {
    if (modalDocumento.value.length < 15) {
        modalDocumento.value += num
    }
}

const eliminarNumeroModal = () => {
    modalDocumento.value = modalDocumento.value.slice(0, -1)
}

const cerrarTecladoModal = () => {
    modalDocumentoFocus.value = false
    if (modalDocumento.value.length >= 5) {
        buscarUsuario()
    }
}

const loadData = async () => {
    loading.value = true
    try {
        const response = await api.get('/tickets/initial-data')
        if (response.data.success) {
            preferencias.value = response.data.data.preferencias
        } else {
            preferencias.value = [
                { id: 1, nombre: 'General', nivel_prioridad: 5 },
                { id: 2, nombre: 'Adulto Mayor', nivel_prioridad: 1 },
                { id: 3, nombre: 'Madre con menor o gestante', nivel_prioridad: 2 },
                { id: 4, nombre: 'Persona con discapacidad', nivel_prioridad: 1 }
            ]
        }
    } catch (error) {
        console.error('Error:', error)
        fallosSeguidos = 2
        conectado.value = false
        motivoDesconexion.value = 'Sin conexión con el servidor. Reconectando…'
        preferencias.value = [
            { id: 1, nombre: 'Normal', nivel_prioridad: 5 },
            { id: 2, nombre: 'Adulto Mayor', nivel_prioridad: 1 },
            { id: 3, nombre: 'Madre con menor o gestante', nivel_prioridad: 2 },
            { id: 4, nombre: 'Persona con discapacidad', nivel_prioridad: 1 }
        ]
    } finally {
        loading.value = false
    }
}

const formatDateTime = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
}

// Impresión directa (sin vista previa ni diálogo de impresora).
// Una página web NO puede saltarse la vista previa por sí sola: la decide el navegador.
// Para imprimir directo, Chrome/Edge debe abrirse en modo kiosco con --kiosk-printing
// (ver iniciar-kiosco.bat). Con esa opción, window.print() envía el ticket de inmediato
// a la impresora predeterminada de Windows (EP802) con la configuración de su driver.
// manual = true  → botón "Reimprimir": muestra el aviso "Imprimiendo..." y alertas de error.
// manual = false → impresión automática al mostrar el ticket: sin avisos que tapen la pantalla.
const printTicket = (manual = false) => {
    if (!ticketGenerado.value) {
        alert('No hay ticket para imprimir')
        return
    }

    // Overlay visual mientras se prepara
    const loadingMsg = document.createElement('div')
    loadingMsg.id = 'print-loading-overlay'
    loadingMsg.style.cssText = `
        position: fixed;
        top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(0,0,0,0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        font-family: Arial, sans-serif;
        font-size: 18px;
        color: white;
        flex-direction: column;
        gap: 20px;
    `
    loadingMsg.innerHTML = `
        <div style="font-size: 40px;">🖨️</div>
        <div>Imprimiendo ticket...</div>
        <div style="font-size: 14px; opacity: 0.7;">Espere un momento</div>
    `
    if (manual === true) document.body.appendChild(loadingMsg)

    const ticket = ticketGenerado.value
    const preferencia = selectedPreferencia.value?.nombre || 'General'
    const tipoTramite = getTipoTramiteNombre.value
    const usuario = datosUsuario.value

    // Escapa texto de usuario para insertarlo con seguridad en el HTML del ticket
    const esc = (texto) => String(texto ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]))

    const fechaHora = new Date(ticket.fecha_hora_solicitud).toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })

    // Ticket optimizado para impresora térmica EP802 (papel de 80 mm).
    // - Ancho imprimible: 72 mm (80 mm de papel con 4 mm de margen a cada lado)
    // - Sólo blanco y negro puro: las térmicas no imprimen grises ni colores
    //   de forma nítida, por eso no hay fondos, emojis ni tonos grises.
    // - Márgenes de página en 0 (el margen se controla con el padding del body)
    // - Espacio final para que la cuchilla no corte sobre el texto
    const contenidoTicket = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <title>Ticket de Atención</title>
        <style>
            @page {
                size: 80mm auto;
                margin: 0;
            }
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
                color: #000 !important;
                background: transparent !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            html, body {
                width: 80mm;
                background: #fff !important;
            }
            body {
                font-family: Arial, Helvetica, sans-serif;
                font-size: 10pt;
                line-height: 1.25;
                padding: 4mm 4mm 12mm;
            }
            .centro { text-align: center; }
            .linea-punteada {
                border-top: 0.4mm dashed #000;
                margin: 2.5mm 0;
            }
            .linea-solida {
                border-top: 0.4mm solid #000;
                margin: 2.5mm 0;
            }

            /* Encabezado */
            .institucion {
                font-size: 10pt;
                font-weight: bold;
                letter-spacing: 0.3px;
                text-transform: uppercase;
            }
            .sistema {
                font-size: 8pt;
                margin-top: 1mm;
                letter-spacing: 0.5px;
            }

            /* Número de ticket */
            .etiqueta-ticket {
                font-size: 8pt;
                font-weight: bold;
                letter-spacing: 2px;
                margin-top: 1mm;
            }
            .numero-ticket {
                font-size: 26pt;
                font-weight: 900;
                letter-spacing: 1px;
                line-height: 1.1;
                margin: 1.5mm 0;
                padding: 2mm 0;
                border: 0.8mm solid #000;
                border-radius: 2mm;
                word-break: break-all;
            }
            .posicion {
                font-size: 10pt;
                font-weight: bold;
                margin: 1mm 0;
            }

            /* Datos */
            .dato { margin: 2mm 0; }
            .dato .titulo {
                font-size: 8pt;
                font-weight: bold;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }
            .dato .valor {
                font-size: 9pt;
                font-weight: bold;
                overflow-wrap: anywhere;
            }
            .fecha-hora {
                font-size: 9pt;
                margin: 1mm 0;
            }

            /* Instrucciones */
            .instrucciones-titulo {
                font-size: 9pt;
                font-weight: bold;
                letter-spacing: 1px;
                margin-bottom: 1mm;
            }
            .instrucciones p {
                font-size: 8pt;
                padding-left: 3mm;
                text-indent: -3mm;
                margin: 0.8mm 0;
            }

            /* Pie */
            .gracias {
                font-size: 10pt;
                font-weight: bold;
                margin-top: 1mm;
            }
            .pie {
                font-size: 7.5pt;
                margin-top: 1mm;
            }
        </style>
    </head>
    <body>
        <div class="centro">
            <div class="institucion">Dirección Departamental<br>de Educación Beni</div>
            <div class="sistema">SISTEMA DE GESTIÓN DE COLAS</div>
        </div>

        <div class="linea-solida"></div>

        <div class="centro">
            <div class="etiqueta-ticket">SU NÚMERO DE TICKET</div>
            <div class="numero-ticket">${esc(ticket.numero_ticket)}</div>
        </div>

        <div class="linea-punteada"></div>

        <div class="dato">
            <div class="titulo">Trámite : ${esc(tipoTramite)}</div>
        </div>

        <div class="dato">
            <div class="titulo">Preferencia : ${esc(preferencia)}</div>
        </div>

        ${usuario ? `
        <div class="dato">
            <div class="titulo">Usuario</div>
            <div class="valor">${esc(usuario.nombre_completo)}</div>
            <div class="fecha-hora">CI: ${esc(usuario.documento)}</div>
        </div>
        ` : ''}

        <div class="dato">
            <div class="titulo">Fecha y hora : ${esc(fechaHora)}</div>
        </div>
        <div class="linea-punteada"></div>
    </body>
    </html>
    `
    // Eliminar iframe previo si quedó colgado
    const prevIframe = document.getElementById('print-iframe')
    if (prevIframe) document.body.removeChild(prevIframe)

    const iframe = document.createElement('iframe')
    iframe.id = 'print-iframe'
    // Invisible pero "renderizado": con visibility:hidden o display:none Chrome puede
    // imprimir el ticket en blanco. Se deja transparente, detrás de la página y sin
    // recibir toques, con dimensiones reales para calcular el layout de 80 mm.
    iframe.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 80mm;
        height: 200mm;
        border: none;
        opacity: 0;
        pointer-events: none;
        z-index: -1;
    `
    document.body.appendChild(iframe)

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
    iframeDoc.open()
    iframeDoc.write(contenidoTicket)
    iframeDoc.close()

    // Esperamos a que el contenido esté completamente cargado
    let impreso = false   // sin diálogo de por medio, una doble llamada sacaría DOS tickets
    const ejecutarImpresion = () => {
        if (impreso) return
        impreso = true
        try {
            // Ocultamos el overlay antes de invocar print()
            // para que no interfiera visualmente si el navegador
            // muestra brevemente algún flash.
            if (document.body.contains(loadingMsg)) {
                document.body.removeChild(loadingMsg)
            }

            iframe.contentWindow.focus()
            iframe.contentWindow.print()

            // Limpieza del iframe: dar tiempo suficiente al spooler
            setTimeout(() => {
                if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe)
                }
            }, 4000)

        } catch (error) {
            console.error('Error al imprimir:', error)
            if (document.body.contains(loadingMsg)) {
                document.body.removeChild(loadingMsg)
            }
            if (document.body.contains(iframe)) {
                document.body.removeChild(iframe)
            }
            if (manual === true) {
                alert('Error al imprimir. Verifique que la impresora esté conectada y configurada como predeterminada.')
            }
        }
    }

    // Usar onload del iframe para asegurarse de que el HTML esté listo
    if (iframe.contentDocument.readyState === 'complete') {
        setTimeout(ejecutarImpresion, 500)
    } else {
        iframe.onload = () => setTimeout(ejecutarImpresion, 500)
        // Fallback por si onload no dispara (algunos navegadores)
        setTimeout(ejecutarImpresion, 1200)
    }
}

const resetForm = () => {
    detenerRetornoTicket()
    ticketGenerado.value = null
    selectedPreferencia.value = null
    selectedTipoTramite.value = null
    usuarioConfirmado.value = false
    datosUsuario.value = null
    posicionEnCola.value = null
    currentStep.value = 1
    modoUsuario.value = 'identificarse'
    modalExito.value = false
    modalUsuarioEncontrado.value = null
    modalDocumento.value = ''
    modalError.value = ''
    reiniciarInactividad() // no hace nada si la pantalla de inicio está activa
}

const getPriorityText = (nivel) => {
    const priorities = {
        1: 'Máxima',
        2: 'Alta',
        3: 'Media',
        4: 'Normal',
        5: 'Estándar'
    }
    return priorities[nivel] || 'Estándar'
}

const EVENTOS_ACTIVIDAD = ['touchstart', 'pointerdown', 'click', 'keydown']

onMounted(() => {
    loadData().finally(programarHeartbeat)
    iniciarReloj()
    window.addEventListener('online', comprobarYa)
    document.addEventListener('visibilitychange', comprobarYa)
    EVENTOS_ACTIVIDAD.forEach((e) => window.addEventListener(e, registrarActividad, { passive: true }))
})

onUnmounted(() => {
    if (relojInterval) clearInterval(relojInterval)
    clearTimeout(heartbeatTimer)
    clearTimeout(errorTicketTimer)
    window.removeEventListener('online', comprobarYa)
    document.removeEventListener('visibilitychange', comprobarYa)
    detenerInactividad()
    detenerRetornoTicket()
    EVENTOS_ACTIVIDAD.forEach((e) => window.removeEventListener(e, registrarActividad))
})
</script>

<style scoped>
/* =====================================================================
   Pantalla objetivo: Elo Touch 1509L en vertical (portrait) 768 x 1366 px
   Se mantiene el diseño original (colores, degradados, formas) y se
   escalan tamaños para uso táctil: botones grandes, tipografía legible
   y contenido que ocupa toda la altura sin scroll.
   ===================================================================== */
.ticket-generator {
    position: relative;
    width: 100%;
    max-width: 768px;
    height: 100vh;
    height: 100dvh;
    margin: 0 auto;
    background: #f5f6f8;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    user-select: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
}

/* ========== HEADER ========== */
.header {
    flex-shrink: 0;
    background: #0f1119;
    padding: 14px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
}

.logo-area {
    display: flex;
    align-items: center;
    gap: 18px;
    flex: 1;
    min-width: 0;
}

.logo {
    height: 96px;
    width: auto;
    max-width: 200px;
    object-fit: contain;
    flex-shrink: 0;
    background: #ffffff;
    padding: 5px 10px;
    border-radius: 15px;
    border: 1px solid rgba(255, 140, 0, 0.2);
}

.title-area h1 {
    margin: 0;
    font-size: 22px;
    line-height: 1.2;
    letter-spacing: 1px;
    color: #ff8c00;
}

.title-area p {
    margin: 6px 0 0;
    font-size: 15px;
    opacity: 0.7;
    color: rgb(250, 249, 249);
}

/* ========== RELOJ ========== */
.reloj-area {
    flex-shrink: 0;
    max-width: 220px;
    text-align: right;
    background: rgba(0, 0, 0, 0.3);
    padding: 10px 16px;
    border-radius: 15px;
    border: 1px solid rgba(255, 140, 0, 0.2);
}

.fecha {
    font-size: 13px;
    line-height: 1.3;
    color: rgba(255, 255, 255, 0.7);
    letter-spacing: 0.5px;
    margin-bottom: 5px;
}

.hora {
    font-size: 30px;
    font-weight: bold;
    font-family: 'Courier New', monospace;
    color: #ff8c00;
    letter-spacing: 2px;
}

/* ========== CONTENIDO PRINCIPAL ========== */
.contenido-principal {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 24px;
    overflow-y: auto;
}

.contenido-principal > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.step-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 0 auto;
    background: #0f1119;
    border-radius: 24px;
    padding: 36px 32px;
    border: 1px solid rgba(255, 140, 0, 0.15);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
}

.step-title {
    text-align: center;
    font-size: 34px;
    color: #ff8c00;
    margin: 0 0 8px;
}

.step-subtitle {
    text-align: center;
    color: rgba(255, 255, 255, 0.6);
    margin: 0 0 30px;
    font-size: 20px;
}

/* ========== TRÁMITES GRID ========== */
/* Una columna: los 4 botones se reparten toda la altura disponible */
.tramites-grid {
    flex: 1;
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: minmax(170px, 1fr);
    gap: 22px;
}

.tramite-btn {
    display: flex;
    align-items: center;
    gap: 28px;
    padding: 28px 32px;
    border: 3px solid transparent;
    border-radius: 22px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: left;
    color: white;
    position: relative;
    min-height: 170px;
}

.tramite-btn:hover {
    transform: translateY(-4px);
    filter: brightness(1.1);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
}

.tramite-btn:active {
    transform: scale(0.98);
    filter: brightness(1.15);
}

.tramite-btn.selected {
    border-color: #ff8c00;
    box-shadow: 0 0 24px rgba(255, 140, 0, 0.35);
}

.tramite-btn .selected-check {
    position: absolute;
    top: 14px;
    right: 22px;
    font-size: 34px;
    font-weight: bold;
    color: #ff8c00;
}

.btn-icon-large {
    flex-shrink: 0;
    width: 104px;
    height: 104px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 22px;
}

.btn-icon-large svg {
    width: 60px;
    height: 60px;
}

/* Colores para tipos de trámite */
.tramite-info {
    background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
}

.tramite-recojo {
    background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
}

.tramite-recepcion {
    background: linear-gradient(135deg, #ff9800 0%, #e65100 100%);
}

.tramite-canje {
    background: linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%);
}

.tramite-btn .btn-content {
    flex: 1;
    min-width: 0;
}

.tramite-btn .btn-title {
    font-weight: bold;
    font-size: 30px;
    letter-spacing: 0.5px;
    display: block;
    margin-bottom: 8px;
}

.tramite-btn .btn-desc {
    font-size: 19px;
    line-height: 1.3;
    opacity: 0.85;
    display: block;
}

/* ========== STEP 2: PREFERENCIA ========== */
.step-preferencia {
    width: 100%;
}

.step-title-preferencia {
    font-size: 32px;
}

.step-subtitle-preferencia {
    margin-bottom: 20px;
}

.back-btn {
    align-self: flex-start;
    background: none;
    border: solid 2px #ff8c00;
    border-radius: 10px;
    color: #ff8c00;
    cursor: pointer;
    font-size: 20px;
    padding: 14px 28px;
    margin-bottom: 20px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.back-btn:hover {
    color: #e67e00;
}

.back-btn:active {
    background: rgba(255, 140, 0, 0.15);
}

/* Botones de usuario */
.usuario-actions {
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
    justify-content: right;
}

.btn-usuario {
    padding: 16px 36px;
    border: 3px solid #ff8c00;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.05);
    color: #ff8c00;
    font-weight: bold;
    font-size: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-usuario:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    background: rgba(255, 255, 255, 0.1);
}

.btn-usuario:active {
    transform: scale(0.97);
    background: rgba(255, 255, 255, 0.15);
}

/* Usuario confirmado */
.usuario-confirmado {
    background: rgba(46, 204, 113, 0.1);
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
    border: 1px solid rgba(46, 204, 113, 0.3);
}

.usuario-info {
    display: flex;
    align-items: center;
    gap: 16px;
}

.usuario-icon {
    font-size: 30px;
}

.usuario-nombre {
    font-weight: bold;
    color: #2ecc71;
    font-size: 22px;
}

.usuario-documento {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.6);
}

.usuario-cambiar {
    margin-left: auto;
    background: #e74c3c;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 10px;
    cursor: pointer;
    font-size: 18px;
    transition: all 0.3s ease;
}

.usuario-cambiar:hover {
    background: #c0392b;
}

/* ========== PREFERENCIAS GRID ========== */
/* Una columna: los 4 botones se reparten la altura disponible */
.preferencias-grid {
    flex: 1;
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: minmax(110px, 1fr);
    gap: 16px;
    margin-bottom: 22px;
}

.preferencia-btn {
    display: flex;
    align-items: center;
    gap: 22px;
    padding: 20px 28px;
    border: 3px solid transparent;
    border-radius: 18px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: left;
    color: white;
    position: relative;
}

.preferencia-btn:hover {
    transform: translateY(-3px);
    filter: brightness(1.1);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
}

.preferencia-btn:active {
    transform: scale(0.98);
    filter: brightness(1.15);
}

.preferencia-btn .pref-check {
    position: absolute;
    top: 12px;
    right: 20px;
    font-size: 28px;
    font-weight: bold;
    color: #fff;
}

/* Colores para preferencias */
.preferencia-normal {
    background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
}

.preferencia-mayor {
    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
}

.preferencia-madre {
    background: linear-gradient(135deg, #e91e63 0%, #c2185b 100%);
}

.preferencia-discapacidad {
    background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
}

/* Icono de preferencia */
.pref-icon-small {
    flex-shrink: 0;
    width: 76px;
    height: 76px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 18px;
}

.pref-icon-small svg {
    width: 46px;
    height: 46px;
}

.pref-emoji {
    font-size: 36px;
}

.pref-content {
    flex: 1;
    min-width: 0;
}

.pref-title {
    font-weight: bold;
    font-size: 26px;
    letter-spacing: 0.5px;
    display: block;
    margin-bottom: 4px;
}

.pref-desc {
    font-size: 18px;
    opacity: 0.8;
}

/* ========== INFO MESSAGE ========== */
.info-message {
    background: rgba(52, 152, 219, 0.1);
    border-left: 6px solid #3498db;
    padding: 18px 22px;
    border-radius: 12px;
    display: flex;
    gap: 16px;
}

.info-icon {
    font-size: 28px;
}

.info-content strong {
    color: rgba(255, 255, 255, 0.9);
    display: block;
    margin-bottom: 4px;
    font-size: 20px;
}

.info-content p {
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
    font-size: 18px;
}

/* ========== MODAL ========== */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: #0f1119;
    border-radius: 24px;
    max-width: 700px;
    width: calc(100% - 48px);
    max-height: 94vh;
    overflow-y: auto;
    border: 1px solid rgba(255, 140, 0, 0.15);
    animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 26px 32px;
    background: rgba(255, 140, 0, 0.08);
    border-bottom: 1px solid rgba(255, 140, 0, 0.1);
    border-radius: 24px 24px 0 0;
}

.modal-header h3 {
    margin: 0;
    font-size: 28px;
    color: #ff8c00;
}

.modal-close {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    font-size: 36px;
    padding: 6px 16px;
    cursor: pointer;
}

.modal-close:hover {
    color: white;
}

.modal-body {
    padding: 30px 32px;
}

.modal-documento label {
    display: block;
    font-weight: 500;
    font-size: 20px;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 12px;
}

.modal-input-group {
    display: flex;
    gap: 12px;
}

.modal-input-group input {
    flex: 1;
    min-width: 0;
    padding: 16px 20px;
    border: 3px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    font-size: 30px;
    font-family: monospace;
    letter-spacing: 3px;
    background: rgba(255, 255, 255, 0.05);
    color: white;
    cursor: pointer;
}

.modal-input-group input:focus {
    outline: none;
    border-color: #ff8c00;
}

.modal-input-group input::placeholder {
    color: rgba(255, 255, 255, 0.3);
}

.modal-limpiar {
    background: #e74c3c;
    color: white;
    border: none;
    border-radius: 12px;
    padding: 12px 22px;
    cursor: pointer;
    font-size: 24px;
}

.modal-error {
    color: #e74c3c;
    font-size: 18px;
    margin-top: 12px;
}

.modal-exito {
    color: #2ecc71;
    font-size: 20px;
    margin-top: 12px;
    padding: 14px;
    background: rgba(46, 204, 113, 0.1);
    border-radius: 12px;
    border: 1px solid rgba(46, 204, 113, 0.2);
}

.modal-exito strong {
    color: #2ecc71;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
}

.modal-btn-buscar {
    padding: 16px 36px;
    background: #ff8c00;
    color: white;
    border: none;
    border-radius: 12px;
    font-weight: bold;
    font-size: 22px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.modal-btn-buscar:hover {
    background: #e67e00;
}

.modal-btn-confirmar {
    padding: 16px 36px;
    background: #2ecc71;
    color: white;
    border: none;
    border-radius: 12px;
    font-weight: bold;
    font-size: 22px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.modal-btn-confirmar:hover {
    background: #27ae60;
}

/* Teclado Modal */
.modal-teclado {
    padding: 22px 32px 32px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(0, 0, 0, 0.2);
    border-radius: 0 0 24px 24px;
}

.teclado-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
}

.tecla {
    min-height: 92px;
    padding: 0;
    border: none;
    border-radius: 16px;
    font-size: 38px;
    font-weight: bold;
    background: rgba(255, 255, 255, 0.08);
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
}

.tecla:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: scale(1.02);
}

.tecla:active {
    transform: scale(0.95);
}

.tecla-borrar {
    background: #e74c3c;
    color: white;
}

.tecla-borrar:hover {
    background: #c0392b;
}

.tecla-aceptar {
    background: #ff8c00;
    color: white;
}

.tecla-aceptar:hover {
    background: #e67e00;
}

/* ========== TICKET GENERADO ========== */
.ticket-result-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    margin: 0 auto;
    animation: slideIn 0.5s ease-out;
}

.ticket-result-card {
    background: #0f1119;
    border-radius: 24px;
    overflow: hidden;
    border: 1px solid rgba(255, 140, 0, 0.15);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
}

.success-header {
    background: linear-gradient(135deg, #ff910b 0%, #ff8c00 100%);
    padding: 32px;
    text-align: center;
}

.success-header h3 {
    color: white;
    margin: 0;
    font-size: 34px;
}

.success-body {
    padding: 36px 32px;
}

.ticket-info {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    padding: 24px 28px;
    margin-bottom: 28px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    padding: 16px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.info-row:last-child {
    border-bottom: none;
}

.info-label {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.5);
    font-size: 18px;
    display: block;
    margin-bottom: 4px;
}

.info-value {
    color: rgba(255, 255, 255, 0.9);
    font-size: 22px;
    font-weight: 500;
    display: block;
    overflow-wrap: anywhere;
}

.ticket-number {
    font-family: monospace;
    font-size: 52px;
    font-weight: bold;
    color: #ff8c00;
}

.position-highlight {
    font-size: 52px;
    font-weight: bold;
    color: #e74c3c;
}

.instructions-card {
    background: rgba(255, 140, 0, 0.08);
    border-radius: 16px;
    padding: 26px 28px;
    margin-bottom: 28px;
    border: 1px solid rgba(255, 140, 0, 0.15);
}

.instructions-title {
    font-size: 24px;
    color: #ff8c00;
    margin-bottom: 16px;
    font-weight: bold;
}

.instructions-list {
    margin: 0;
    padding-left: 28px;
}

.instructions-list li {
    color: rgba(255, 255, 255, 0.7);
    margin: 12px 0;
    font-size: 20px;
}

.button-group {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
}

.print-btn {
    padding: 24px 12px;
    background: #ff8c00;
    color: white;
    border: none;
    border-radius: 14px;
    cursor: pointer;
    font-weight: bold;
    font-size: 24px;
    transition: all 0.3s ease;
}

.print-btn:hover {
    background: #e67e00;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 140, 0, 0.3);
}

.print-btn:active {
    transform: scale(0.97);
}

.new-ticket-btn {
    padding: 24px 12px;
    background: #2c3e50;
    color: white;
    border: none;
    border-radius: 14px;
    cursor: pointer;
    font-weight: bold;
    font-size: 24px;
    transition: all 0.3s ease;
}

.new-ticket-btn:hover {
    background: #1a252f;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.new-ticket-btn:active {
    transform: scale(0.97);
}

/* ========== LOADING ========== */
.loading {
    align-items: center;
    justify-content: center;
    min-height: 400px;
}

.loading p {
    font-size: 24px;
}

.spinner {
    width: 80px;
    height: 80px;
    border: 5px solid rgba(255, 140, 0, 0.15);
    border-top: 5px solid #ff8c00;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 20px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

@keyframes slideIn {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

/* ========== PANTALLA DE INICIO ========== */
.pantalla-bienvenida {
    position: absolute;
    inset: 0;
    z-index: 1500;
    background: #050a14;
    overflow: hidden;
    cursor: pointer;
}

.fade-inicio-enter-active,
.fade-inicio-leave-active {
    transition: opacity 0.4s ease;
}

.fade-inicio-enter-from,
.fade-inicio-leave-to {
    opacity: 0;
}

/* La imagen es 9:16, igual que la pantalla vertical de 768 x 1366 */
.bienvenida-img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    pointer-events: none;
}

/* Brillo pulsante sobre el botón "TOCA LA PANTALLA PARA INICIAR" de la imagen */
.bienvenida-pulso {
    position: absolute;
    left: 10.3%;
    top: 73.4%;
    width: 79.5%;
    height: 8.85%;
    border-radius: 999px;
    pointer-events: none;
    animation: pulsoToque 1.8s ease-in-out infinite;
}

@keyframes pulsoToque {
    0%, 100% {
        box-shadow: 0 0 0 0 rgba(255, 140, 0, 0), 0 0 18px rgba(255, 140, 0, 0.25);
    }
    50% {
        box-shadow: 0 0 0 10px rgba(255, 140, 0, 0.12), 0 0 46px rgba(255, 140, 0, 0.6);
    }
}

/* Respaldo (si falta la imagen) */
.bienvenida-respaldo {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 36px;
    padding: 40px;
    text-align: center;
    background:
        radial-gradient(ellipse 80% 50% at 50% 40%, #0b1f40 0%, transparent 70%),
        #050a14;
}

.respaldo-logo {
    height: 150px;
    max-width: 90%;
    object-fit: contain;
    background: #ffffff;
    padding: 10px 24px;
    border-radius: 28px;
}

.respaldo-titulo {
    margin: 0;
    font-size: 96px;
    font-weight: 900;
    letter-spacing: 2px;
    color: #ffffff;
}

.respaldo-sub {
    margin: 0;
    font-size: 30px;
    font-weight: 800;
    letter-spacing: 2px;
    color: #ffffff;
}

.respaldo-sub span {
    color: #ff8c00;
}

.respaldo-boton {
    margin-top: 40px;
    padding: 30px 60px;
    border: 3px solid #ff8c00;
    border-radius: 999px;
    font-size: 36px;
    font-weight: 800;
    color: #ffffff;
    box-shadow: 0 0 30px rgba(255, 140, 0, 0.35);
    animation: pulsoToque 1.8s ease-in-out infinite;
}

.respaldo-boton span {
    color: #ff8c00;
}

/* Aviso en la pantalla del ticket */
.retorno-aviso {
    margin-top: 26px;
    text-align: center;
    font-size: 19px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.65);
}

.retorno-aviso strong {
    color: #ff8c00;
    font-size: 24px;
}

/* ========== IMPRESIÓN ========== */
@media print {
    .header,
    .button-group,
    .print-btn,
    .modal-overlay {
        display: none;
    }

    .ticket-generator {
        height: auto;
        overflow: visible;
    }

    .ticket-result-card {
        border: 1px solid #ddd;
        box-shadow: none;
    }

    .success-body {
        padding: 20px;
    }
}

/* ===== Aviso de conexión / error de ticket ===== */
.banner-conexion {
    position: absolute;
    top: 0; left: 0; right: 0;
    z-index: 10000;
    padding: 14px 20px;
    background: #b71c1c;
    color: #fff;
    font-size: 24px;
    font-weight: 700;
    text-align: center;
}
.toast-error-ticket {
    position: absolute;
    left: 50%;
    bottom: 140px;
    transform: translateX(-50%);
    z-index: 10000;
    max-width: 85%;
    padding: 24px 32px;
    background: #b71c1c;
    color: #fff;
    font-size: 28px;
    font-weight: 700;
    text-align: center;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}
</style>
